import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, CalendarRange, Facebook, Instagram, Clock, AlertTriangle, ExternalLink, Loader2, Sparkles, ImageOff, MessageCircle, Send, Music, Trash2, Upload, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import TikTokIcon from '../components/icons/TikTokIcon';
import AIPlanDialog from '../components/social/AIPlanDialog';

const platformIcons = { facebook: Facebook, instagram: Instagram, tiktok: TikTokIcon };

const AccountRow = ({ account, onSave, onOpen, onPlan, onDigest }) => {
  const [limit, setLimit] = useState(account.settings.monthly_post_limit);
  const [mode, setMode] = useState(account.settings.approval_mode);
  const [digest, setDigest] = useState(account.settings.weekly_digest !== false);
  const [saving, setSaving] = useState(false);
  const dirty = limit !== account.settings.monthly_post_limit || mode !== account.settings.approval_mode || digest !== (account.settings.weekly_digest !== false);

  const save = async () => {
    setSaving(true);
    await onSave(account.id, { monthly_post_limit: Number(limit), approval_mode: mode, weekly_digest: digest });
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center rounded-lg border p-3" data-testid={`agency-row-${account.id}`}>
      <div className="lg:col-span-3 flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate" data-testid={`agency-brand-${account.id}`}>{account.brand}</p>
          <p className="text-xs text-muted-foreground truncate">{account.email}{account.city ? ` · ${account.city}` : ''}</p>
        </div>
      </div>
      <div className="lg:col-span-2 flex items-center gap-1.5">
        {account.connections.length === 0 && <span className="text-xs text-muted-foreground">Nessun social</span>}
        {account.connections.map(p => { const Icon = platformIcons[p]; return Icon ? <Icon key={p} className="w-4 h-4 text-muted-foreground" /> : null; })}
      </div>
      <div className="lg:col-span-2 flex flex-wrap gap-1.5 text-xs">
        <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" /> {account.stats.upcoming} in coda</Badge>
        {account.stats.pending > 0 && <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300">{account.stats.pending} da approvare</Badge>}
        {account.stats.needs_media > 0 && <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300"><ImageOff className="w-3 h-3" /> {account.stats.needs_media}</Badge>}
      </div>
      <div className="lg:col-span-3 flex items-center gap-2">
        <div className="flex items-center gap-1 text-xs whitespace-nowrap">
          <Sparkles className="w-3.5 h-3.5 text-primary" /> {account.usage.used}/
          <Input type="number" min={0} value={limit} onChange={e => setLimit(Number(e.target.value))} className="h-8 w-16 text-xs" data-testid={`agency-limit-${account.id}`} />
        </div>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="h-8 text-xs" data-testid={`agency-mode-${account.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="shop_approves">Approva il negozio</SelectItem>
            <SelectItem value="direct">Pubblicazione diretta</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer" title="Riepilogo WhatsApp del lunedì">
          <Switch checked={digest} onCheckedChange={setDigest} className="scale-75" data-testid={`agency-digest-${account.id}`} />
          <MessageCircle className="w-3.5 h-3.5 text-muted-foreground" />
        </label>
        {dirty && (
          <Button size="sm" variant="secondary" className="h-8" onClick={save} disabled={saving} data-testid={`agency-save-${account.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Salva'}
          </Button>
        )}
      </div>
      <div className="lg:col-span-2 flex gap-2 justify-end">
        <Button size="sm" variant="ghost" className="h-8" onClick={() => onDigest(account)} title="Anteprima riepilogo WhatsApp" data-testid={`agency-digest-preview-${account.id}`}>
          <Send className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => onPlan(account)} data-testid={`agency-plan-${account.id}`}>
          <CalendarRange className="w-3.5 h-3.5 mr-1" /> Piano AI
        </Button>
        <Button size="sm" className="h-8" onClick={() => onOpen(account)} data-testid={`agency-open-${account.id}`}>
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Calendario
        </Button>
      </div>
    </div>
  );
};

const AgencyPage = () => {
  const { API, getAuthHeaders, setActAs } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planAccount, setPlanAccount] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [uploadingTrack, setUploadingTrack] = useState(false);
  const [runningReports, setRunningReports] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [a, u] = await Promise.all([
        axios.get(`${API}/ai-social/admin/accounts`, getAuthHeaders()),
        axios.get(`${API}/ai-social/admin/upcoming?days=14`, getAuthHeaders()),
      ]);
      setAccounts(a.data);
      setUpcoming(u.data);
      axios.get(`${API}/ai-social/music`, getAuthHeaders()).then(r => setTracks(r.data)).catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => () => setActAs(null), [setActAs]);

  const saveSettings = async (userId, settings) => {
    try {
      await axios.put(`${API}/ai-social/admin/accounts/${userId}/settings`, settings, getAuthHeaders());
      toast.success('Impostazioni salvate');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const openCalendar = (account) => navigate(`/social?account=${account.id}`);

  const uploadTrack = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTrack(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      const res = await axios.post(`${API}/ai-social/admin/music/upload`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      setTracks(prev => [res.data, ...prev]);
      toast.success('Brano aggiunto alla libreria Reel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore caricamento brano');
    } finally {
      setUploadingTrack(false);
      e.target.value = '';
    }
  };

  const deleteTrack = async (id) => {
    try {
      await axios.delete(`${API}/ai-social/admin/music/${id}`, getAuthHeaders());
      setTracks(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      toast.error('Errore eliminazione brano');
    }
  };

  const runReports = async () => {
    setRunningReports(true);
    try {
      const res = await axios.post(`${API}/ai-social/admin/reports/run?send=false`, {}, getAuthHeaders());
      const ok = res.data.filter(r => r.url).length;
      toast.success(`${ok} report generati (mese scorso). Invio automatico il 1° del mese.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione report');
    } finally {
      setRunningReports(false);
    }
  };

  const previewDigest = async (account) => {
    try {
      const res = await axios.post(`${API}/ai-social/admin/digest/run?dry_run=true&user_id=${account.id}&horizon_days=7`, {}, getAuthHeaders());
      setDigestPreview({ account, result: res.data[0] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore anteprima riepilogo');
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/ai-social/admin/digest/run?dry_run=false&user_id=${digestPreview.account.id}&horizon_days=7`, {}, getAuthHeaders());
      const r = res.data[0];
      if (r.skipped) toast.info(`Non inviato: ${r.skipped}`);
      else if (r.whatsapp?.success) toast.success('Riepilogo inviato su WhatsApp');
      else toast.warning(`Notifica in-app inviata. WhatsApp: ${r.whatsapp?.error || 'non inviato'}`);
      setDigestPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio riepilogo');
    } finally {
      setSendingDigest(false);
    }
  };
  const openPlan = (account) => { setActAs(account.id); setPlanAccount(account); };
  const closePlan = (open) => { if (!open) { setPlanAccount(null); setActAs(null); fetchAll(); } };

  const totals = accounts.reduce((acc, a) => ({
    pending: acc.pending + a.stats.pending, upcoming: acc.upcoming + a.stats.upcoming, needs_media: acc.needs_media + a.stats.needs_media,
  }), { pending: 0, upcoming: 0, needs_media: 0 });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="agency-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Agenzia AI</h1>
          <p className="text-muted-foreground mt-1">Gestisci i calendari social di tutti i negozi con gli agenti AI</p>
        </div>
        <Button variant="outline" onClick={runReports} disabled={runningReports} data-testid="agency-run-reports-btn">
          {runningReports ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} Genera report mensili
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Negozi gestiti', value: accounts.length, Icon: Building2 },
          { label: 'Post in coda (tutti)', value: totals.upcoming, Icon: Clock },
          { label: 'In attesa di approvazione', value: totals.pending, Icon: AlertTriangle },
          { label: 'Senza foto', value: totals.needs_media, Icon: ImageOff },
        ].map(({ label, value, Icon }) => (
          <Card key={label}><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
            <div><p className="text-2xl font-bold font-outfit">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-outfit text-base">Negozi</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {accounts.length === 0 && <p className="text-sm text-muted-foreground">Nessun account negozio.</p>}
          {accounts.map(a => <AccountRow key={a.id} account={a} onSave={saveSettings} onOpen={openCalendar} onPlan={openPlan} onDigest={previewDigest} />)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-outfit text-base">Prossimi 14 giorni · tutti i negozi</CardTitle></CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="agency-upcoming-empty">Nessun post programmato nei prossimi 14 giorni.</p>
          ) : (
            <div className="space-y-2" data-testid="agency-upcoming-list">
              {upcoming.map(p => (
                <div key={p.id} className="flex items-start gap-3 rounded-lg border p-2.5 text-sm">
                  <div className="text-xs text-muted-foreground w-24 shrink-0 pt-0.5">{format(parseISO(p.scheduled_time), 'EEE d MMM HH:mm', { locale: it })}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                      <span className="font-medium text-xs">{p.account_name}</span>
                      {p.platforms.map(pl => { const Icon = platformIcons[pl]; return Icon ? <Icon key={pl} className="w-3 h-3 text-muted-foreground" /> : null; })}
                      {p.post_status === 'pending_approval' && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">In approvazione</Badge>}
                      {p.needs_media && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Senza foto</Badge>}
                      {p.ai_generated && <Sparkles className="w-3 h-3 text-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{p.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="agency-music-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-outfit text-base flex items-center gap-2"><Music className="w-4 h-4 text-primary" /> Musiche per i Reel</CardTitle>
          <label className="inline-flex">
            <Button size="sm" variant="outline" asChild disabled={uploadingTrack}>
              <span className="cursor-pointer" data-testid="agency-music-upload-btn">{uploadingTrack ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Carica brano</span>
            </Button>
            <input type="file" accept="audio/*" className="hidden" onChange={uploadTrack} data-testid="agency-music-upload-input" />
          </label>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">Carica solo brani royalty-free di cui hai i diritti. I negozi li scelgono quando creano un Reel; in alternativa è sempre disponibile una traccia ambient generata.</p>
          {tracks.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="agency-music-empty">Nessun brano caricato.</p> : (
            <div className="space-y-1.5">
              {tracks.map(t => (
                <div key={t.id} className="flex items-center gap-3 rounded-lg border p-2 text-sm" data-testid={`agency-music-row-${t.id}`}>
                  <Music className="w-4 h-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{t.title}</span>
                  <audio src={`${API.replace('/api', '')}${t.media_url}`} controls preload="none" className="h-8 w-48 hidden sm:block" />
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => deleteTrack(t.id)} data-testid={`agency-music-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!digestPreview} onOpenChange={(o) => { if (!o) setDigestPreview(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2"><MessageCircle className="w-5 h-5 text-primary" /> Riepilogo settimanale</DialogTitle>
            <DialogDescription>Anteprima del messaggio WhatsApp per {digestPreview?.account.brand}. Viene inviato automaticamente ogni lunedì alle 9:00.</DialogDescription>
          </DialogHeader>
          {digestPreview?.result?.skipped ? (
            <p className="text-sm text-muted-foreground" data-testid="digest-preview-skipped">Nessun riepilogo da inviare: {digestPreview.result.skipped}.</p>
          ) : (
            <>
              <pre className="whitespace-pre-wrap text-sm rounded-lg bg-muted p-3 font-sans" data-testid="digest-preview-message">{digestPreview?.result?.message}</pre>
              <p className="text-xs text-muted-foreground">Destinatario: {digestPreview?.result?.phone || 'telefono non impostato nel profilo azienda (verrà inviata solo la notifica in-app)'}</p>
              <div className="flex justify-end">
                <Button onClick={sendDigest} disabled={sendingDigest} data-testid="digest-send-btn">
                  {sendingDigest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Invia adesso
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {planAccount && (
        <AIPlanDialog open={!!planAccount} onOpenChange={closePlan} onSaved={fetchAll} usage={{ acting_as: true }} />
      )}
    </div>
  );
};

export default AgencyPage;
